import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Loader2, Search, MapPin } from "lucide-react";

// Fix default marker icon paths (webpack doesn't bundle them automatically)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [-1.9536, 30.0606]; // Kigali, Rwanda
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_REVERSE = "https://nominatim.openstreetmap.org/reverse";

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 0.6 });
  }, [position, map]);
  return null;
}

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export function LocationDisplay({ lat, lng, label, height = 280 }) {
  if (lat == null || lng == null) return null;
  const pos = [parseFloat(lat), parseFloat(lng)];
  return (
    <div className="rounded-md overflow-hidden border border-border" style={{ height }} data-testid="location-map-display">
      <MapContainer center={pos} zoom={14} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={pos} />
      </MapContainer>
      {label && <div className="px-3 py-2 text-xs text-muted-foreground bg-card border-t border-border">{label}</div>}
    </div>
  );
}

export default function LocationPicker({ lat, lng, address, onChange }) {
  const initial = lat != null && lng != null ? [parseFloat(lat), parseFloat(lng)] : DEFAULT_CENTER;
  const [position, setPosition] = useState(lat != null && lng != null ? initial : null);
  const [query, setQuery] = useState(address || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debRef = useRef(null);

  const update = (lat, lng, addr) => {
    setPosition([lat, lng]);
    if (addr !== undefined) setQuery(addr);
    onChange?.({ lat, lng, address: addr ?? query });
  };

  const search = useCallback(async (q) => {
    if (!q || q.length < 3) { setResults([]); return; }
    setSearching(true);
    try {
      const url = `${NOMINATIM_URL}?format=json&q=${encodeURIComponent(q)}&limit=6&addressdetails=1`;
      const resp = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await resp.json();
      setResults(data || []);
      setOpen(true);
    } catch (e) { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const onQueryChange = (v) => {
    setQuery(v);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => search(v), 450);
  };

  const commitTypedAddress = async () => {
    if (!query) return;
    if (results.length > 0) {
      pickResult(results[0]);
      return;
    }
    try {
      const url = `${NOMINATIM_URL}?format=json&q=${encodeURIComponent(query)}&limit=1&addressdetails=1`;
      const resp = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await resp.json();
      if (data && data[0]) {
        pickResult(data[0]);
      } else {
        onChange?.({ lat: position ? position[0] : null, lng: position ? position[1] : null, address: query });
      }
    } catch (e) {
      onChange?.({ lat: position ? position[0] : null, lng: position ? position[1] : null, address: query });
    }
  };

  const pickResult = (r) => {
    const la = parseFloat(r.lat);
    const ln = parseFloat(r.lon);
    update(la, ln, r.display_name);
    setOpen(false);
  };

  const handleMapClick = async ({ lat: la, lng: ln }) => {
    update(la, ln);
    // Reverse-geocode
    try {
      const url = `${NOMINATIM_REVERSE}?format=json&lat=${la}&lon=${ln}`;
      const resp = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await resp.json();
      if (data?.display_name) update(la, ln, data.display_name);
    } catch (e) {/* ignore */}
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      <Label className="text-xs">Pickup address (type, search, or click on map)</Label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
        <Input
          className="pl-9"
          placeholder="e.g. Kacyiru, KK 24 Ave"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={commitTypedAddress}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitTypedAddress(); setOpen(false); } }}
          data-testid="address-search-input"
        />
        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
        {open && results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-md shadow-organic max-h-64 overflow-auto" data-testid="address-suggestions">
            {results.map((r) => (
              <button
                type="button"
                key={r.place_id}
                onClick={() => pickResult(r)}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-b border-border last:border-b-0"
                data-testid={`address-suggestion-${r.place_id}`}
              >
                <div className="flex items-start gap-2">
                  <MapPin size={12} className="mt-0.5 shrink-0 text-accent" />
                  <span className="truncate">{r.display_name}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-md overflow-hidden border border-border" style={{ height: 280 }}>
        <MapContainer center={initial} zoom={position ? 14 : 11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onClick={handleMapClick} />
          {position && <Marker position={position} />}
          {position && <FlyTo position={position} />}
        </MapContainer>
      </div>
      <p className="text-xs text-muted-foreground">Tip: click anywhere on the map to drop the pin precisely.</p>
    </div>
  );
}
